import { useLanguage } from "@/context/LanguageContext";
import { buildWaLink } from "@/lib/whatsapp";
import { ArrowUpRight, Award, MapPin } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import Services from "@/components/sections/Services";
import How from "@/components/sections/How";

/* Simplified preview — comparison only, not linked from site.
   Changes:
   1) Neutral hero image (interior, no person with tools)
   2) Single hero CTA (no secondary "See how I help")
   3) Legacy pill without "EST. RIYADH" small label
   4) Simpler Coverage (light background, subtle chips)
   5) FAQ removed
   6) Dark "Say hi" CTA block removed
*/

const SimplifiedHero = () => {
  const { t, lang, isRTL } = useLanguage();
  return (
    <section className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden">
      <div aria-hidden className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-20%,rgba(255,90,0,0.10),transparent_60%)]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 h-8 px-4 rounded-full bg-white border border-[#E2DFD2] text-xs font-bold uppercase tracking-[0.18em] text-[#0B1320]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#25D366]" />
              {t("hero.eyebrow")}
            </div>

            <h1
              className="mt-6 font-black tracking-tight leading-[0.98] text-[#0B1320]"
              style={{
                fontSize: "clamp(2.5rem, 6.5vw, 5rem)",
                fontFamily: lang === "ar" ? "Tajawal, sans-serif" : "Outfit, sans-serif",
              }}
            >
              <span className="block">{t("hero.titleLine1")}</span>
              <span className="block text-[#0B1320]/40">{t("hero.titleLine2")}</span>
            </h1>

            <p className="mt-6 max-w-xl text-lg md:text-xl leading-relaxed text-[#475569]">
              {t("hero.subtitle")}
            </p>

            {/* Simplified legacy pill — no small label */}
            <div className="mt-6 inline-flex items-center gap-3 py-2.5 pe-5 ps-2.5 rounded-full bg-white border border-[#E2DFD2]">
              <span className="h-8 w-8 rounded-full bg-[#0B1320] text-[#FF5A00] grid place-items-center shrink-0">
                <Award size={16} strokeWidth={2.5} />
              </span>
              <span className="text-sm font-bold text-[#0B1320] leading-tight">
                {t("hero.legacy")}
              </span>
            </div>

            {/* Single CTA only */}
            <div className="mt-9">
              <a
                href={buildWaLink(t("whatsapp.msg_default"))}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 h-14 px-7 rounded-full bg-[#25D366] text-white text-base font-bold hover:brightness-110 transition-all shadow-[0_10px_30px_rgba(37,211,102,0.30)] hover:-translate-y-0.5"
              >
                {t("hero.cta_primary")}
                <ArrowUpRight size={18} className={isRTL ? "scale-x-[-1]" : ""} />
              </a>
            </div>
          </div>

          {/* Image options grid — user can pick */}
          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-3">
              {[
                { id: "A", label: "Pendant lights", src: "https://images.unsplash.com/photo-1513506003901-1e6a229e2d15?auto=format&fit=crop&w=800&q=80" },
                { id: "B", label: "Cozy living room", src: "https://images.unsplash.com/photo-1567767292278-a4f21aa2d36e?auto=format&fit=crop&w=800&q=80" },
                { id: "C", label: "Edison bulb", src: "https://images.unsplash.com/photo-1519710164239-da123dc03ef4?auto=format&fit=crop&w=800&q=80" },
                { id: "D", label: "Tools flatlay", src: "https://images.unsplash.com/photo-1581147036324-c47a03a81d48?auto=format&fit=crop&w=800&q=80" },
              ].map(({ id, label, src }) => (
                <div key={id} className="relative rounded-2xl overflow-hidden aspect-square shadow-lg">
                  <img src={src} alt={label} className="w-full h-full object-cover" />
                  <div className="absolute bottom-2 start-2 h-7 px-2.5 rounded-full bg-black/70 backdrop-blur text-white text-[10px] font-bold uppercase tracking-[0.15em] flex items-center">
                    {id} · {label}
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-[#475569] text-center">
              Bolo <span className="font-bold text-[#0B1320]">"Option A"</span> / B / C / D — main apply kar dunga
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const SimplifiedCoverage = () => {
  const { t, lang } = useLanguage();
  const areas = t("coverage.areas") || [];
  return (
    <section id="coverage" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A00]">
            {t("coverage.eyebrow")}
          </div>
          <h2
            className="mt-4 text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-[#0B1320] leading-[1.05]"
            style={{ fontFamily: lang === "ar" ? "Tajawal, sans-serif" : "Outfit, sans-serif" }}
          >
            {t("coverage.title")}
          </h2>
          <p className="mt-5 text-lg text-[#475569] max-w-2xl">
            {t("coverage.subtitle")}
          </p>
        </div>

        <div className="mt-10 flex items-center gap-3 flex-wrap">
          <MapPin size={16} className="text-[#FF5A00]" />
          {areas.map((a, i) => (
            <span
              key={i}
              className={`inline-flex items-center h-10 px-4 rounded-full text-sm font-bold transition-colors ${
                i === 0
                  ? "bg-[#FF5A00] text-white"
                  : "bg-[#FAF9F6] text-[#0B1320] border border-[#E2DFD2]"
              }`}
            >
              {a}
            </span>
          ))}
          <span className="inline-flex items-center h-10 px-4 rounded-full text-sm font-semibold bg-white text-[#475569] border border-dashed border-[#E2DFD2]">
            + {t("coverage.soon")}
          </span>
        </div>
      </div>
    </section>
  );
};

const PreviewSimplified = () => {
  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <div className="bg-[#FF5A00] text-white text-center text-xs font-bold uppercase tracking-[0.15em] py-2 z-50 relative">
        Preview only · Not live
      </div>
      <Header />
      <main>
        <SimplifiedHero />
        <Services />
        <How />
        <SimplifiedCoverage />
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default PreviewSimplified;
